import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Guwahati, Assam — where Apollo Clinic actually is, so a browser with no
// patient data yet (or every point failing to geocode) still opens on the
// clinic's own catchment instead of the middle of the ocean at [0, 0].
const FALLBACK_CENTER = [26.1445, 91.7362];
const FALLBACK_ZOOM = 7;

// Sized in three tiers so a district of 3 doesn't visually compete with one
// of 300 — bucket boundaries are arbitrary but keep small counts legible at
// a glance instead of every bubble being the same size.
const districtIcon = (count) => {
  const size = count < 10 ? 32 : count < 100 ? 42 : 52;
  const fontSize = count < 10 ? 12 : count < 100 ? 13 : 14;
  return L.divIcon({
    // The label is its own absolutely-centered element rather than relying
    // on flex centering on the icon's root div — that root div is also
    // where Leaflet applies its own inline positioning styles, which makes
    // flex centering on it unreliable (the number rendered top-left of the
    // circle instead of centered).
    html: `<span class="patient-map-cluster-label" style="font-size:${fontSize}px">${count}</span>`,
    className: "patient-map-cluster",
    iconSize: L.point(size, size, true),
  });
};

// Leaflet needs an imperative `map.fitBounds` call — there's no declarative
// prop for "frame these points" — so this runs it once the points arrive.
function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 11 });
  }, [points, map]);
  return null;
}

// Leaflet measures its container once on mount and caches that size — it
// has no way to notice the fullscreen toggle resizing that container out
// from under it, so without this the map stays cropped to its old
// dimensions (or shows grey gaps) after expanding/collapsing. The delay
// waits out the CSS transition so it measures the *settled* size.
//
// scrollWheelZoom is toggled the same imperative way rather than relying on
// the MapContainer prop — react-leaflet only applies that prop at initial
// creation, not on every re-render, so flipping it after mount needs the
// underlying Leaflet handler's own enable()/disable().
function InvalidateOnResize({ trigger, scrollZoom }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 260);
    return () => clearTimeout(t);
  }, [trigger, map]);
  useEffect(() => {
    if (scrollZoom) map.scrollWheelZoom.enable();
    else map.scrollWheelZoom.disable();
  }, [scrollZoom, map]);
  return null;
}

/**
 * One marker per district (not per pincode, not radius-clustered) — the
 * number on a bubble is always exactly that district's patient count, the
 * same number the "Top districts" panel shows, at any zoom level. The
 * earlier version plotted one dot per patient and let leaflet.markercluster
 * group them by screen-pixel distance at the current zoom; that group could
 * straddle two nearby districts (or split one district across two bubbles),
 * so its number never quite matched the panel and looked "unfiltered" or
 * wrong even when the underlying data was current. `points` here is
 * pre-aggregated to one row per district by the caller (Stats.jsx), each
 * with a centroid lat/lng — no clustering library needed at all.
 */
export default function PatientGeoMap({ points, expanded }) {
  return (
    <MapContainer
      center={FALLBACK_CENTER}
      zoom={FALLBACK_ZOOM}
      // Scroll-zoom is disabled inline on the page (scrolling to read the
      // rest of Stats shouldn't get hijacked mid-way), but fair game once
      // the map has the whole screen to itself.
      scrollWheelZoom={expanded}
      className="h-full w-full rounded-md"
    >
      {/* Esri's "Light Gray Canvas" — a quiet basemap that lets colored data
          markers be the focal point, via Esri's always-free ArcGIS Online
          tile service (CARTO's anonymous endpoint now silently requires an
          API key — see git history). Esri splits base (land/water shapes)
          and reference (labels/roads) into two layers, stacked here. */}
      {/* maxNativeZoom (not maxZoom) caps the tile *request*, not how far
          the map can zoom — past 16 it just upscales the z16 tile, which
          beats refusing to zoom in further at all. */}
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
        maxNativeZoom={16}
        maxZoom={19}
        attribution="Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ"
      />
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
        maxNativeZoom={16}
        maxZoom={19}
      />
      <FitToPoints points={points} />
      <InvalidateOnResize trigger={expanded} scrollZoom={expanded} />
      {points.map((d) => (
        <Marker key={d.name} position={[d.lat, d.lng]} icon={districtIcon(d.count)}>
          <Popup>
            <div className="text-sm">
              <p className="font-medium">{d.name}</p>
              <p className="mt-1">
                <b>{d.count}</b> patient{d.count === 1 ? "" : "s"}
              </p>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
